import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Space, Button, message, Row, Col, Statistic, Segmented, Drawer, Descriptions, Timeline, Modal, Form, Input } from 'antd';
import { EyeOutlined, TruckOutlined } from '@ant-design/icons';
import request from '../utils/request';

const statusMap = {
  0: { color: 'orange', text: '待付款' },
  1: { color: 'blue', text: '待发货' },
  2: { color: 'cyan', text: '待收货' },
  3: { color: 'green', text: '已完成' },
  4: { color: 'red', text: '已取消' },
};
const statusLabels = { 全部: null, 待付款: 0, 待发货: 1, 待收货: 2, 已完成: 3, 已取消: 4 };
const returnStatusMap = {
  0: '待审核', 1: '审核通过', 2: '审核拒绝', 3: '待寄回', 4: '已寄回',
  5: '验收中', 6: '验收通过', 7: '验收不通过', 8: '退款完成', 9: '已取消'
};

const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderView, setOrderView] = useState('全部');
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [shipOrder, setShipOrder] = useState(null);
  const [shipForm] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const fetchOrders = async (page = 1, pageSize = 10, view = orderView) => {
    setLoading(true);
    try {
      const response = await request.get('/admin/order/list', {
        params: { page, pageSize, orderStatus: statusLabels[view] }
      });
      if (response.success) {
        setOrders(response.data.list || response.data || []);
        setPagination(prev => ({ ...prev, current: page, pageSize, total: response.data.total || 0 }));
      }
    } catch (error) {
      message.error('获取订单列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(1, pagination.pageSize, orderView); }, [orderView]);

  const viewDetail = async (record) => {
    try {
      const response = await request.get(`/admin/order/detail/${record.id}`);
      if (response.success) {
        setDetail(response.data);
        setDetailOpen(true);
      }
    } catch (_) { message.error('获取订单详情失败'); }
  };

  const submitShip = async () => {
    try {
      const values = await shipForm.validateFields();
      const response = await request.post(`/admin/order/ship/${shipOrder.id}`, values);
      if (response.success) {
        message.success('发货成功');
        setShipOrder(null);
        shipForm.resetFields();
        fetchOrders(pagination.current, pagination.pageSize);
      }
    } catch (error) { if (!error?.errorFields) message.error('发货失败'); }
  };

  const columns = [
    { title: '订单号', dataIndex: 'order_no', key: 'order_no', width: 190 },
    { title: '客户', key: 'user', width: 140, render: (_, r) => r.user?.nickname || r.user?.phone || r.receiver_name || '-' },
    { title: '商品', key: 'items', width: 180, render: (_, r) => `${r.orderItems?.[0]?.product_name || r.orderItems?.[0]?.product?.name || '-'}${r.orderItems?.length > 1 ? ` 等${r.orderItems.length}件` : ''}` },
    { title: '实付金额', dataIndex: 'pay_amount', key: 'pay_amount', width: 110, render: (v, r) => `¥${Number(v ?? r.total_amount ?? 0).toFixed(2)}` },
    { title: '优惠', dataIndex: 'discount_amount', key: 'discount_amount', width: 90, render: v => `-¥${Number(v || 0).toFixed(2)}` },
    { title: '状态', dataIndex: 'order_status', key: 'status', width: 100, render: s => <Tag color={(statusMap[s] || {}).color}>{(statusMap[s] || { text: '未知' }).text}</Tag> },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 170, render: d => d ? new Date(d).toLocaleString() : '-' },
    { title: '操作', key: 'action', fixed: 'right', width: 170, render: (_, record) => <Space>
      <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => viewDetail(record)}>详情</Button>
      {Number(record.order_status) === 1 && <Button type="link" size="small" icon={<TruckOutlined />} onClick={() => { setShipOrder(record); shipForm.resetFields(); }}>发货</Button>}
    </Space> },
  ];

  const statusCounts = orders.reduce((counts, order) => { const s = order.order_status ?? order.status; counts[s] = (counts[s] || 0) + 1; return counts; }, {});
  const pageAmount = orders.reduce((sum, order) => sum + Number(order.pay_amount || order.total_amount || 0), 0);

  return <div className="workspace-page">
    <div className="workspace-header"><div><h1 className="workspace-title">订单管理</h1><p className="workspace-description">查看交易金额、商品快照和履约状态，处理已支付的待发货订单</p></div></div>
    <Row gutter={[16, 16]} className="workspace-summary">
      <Col xs={12} lg={6}><Card size="small"><Statistic title="订单总数" value={pagination.total} /></Card></Col>
      <Col xs={12} lg={6}><Card size="small"><Statistic title="当前页实付" value={pageAmount} precision={2} prefix="¥" /></Card></Col>
      <Col xs={12} lg={6}><Card size="small"><Statistic title="当前页待付款" value={statusCounts[0] || 0} /></Card></Col>
      <Col xs={12} lg={6}><Card size="small"><Statistic title="当前页待发货" value={statusCounts[1] || 0} /></Card></Col>
    </Row>
    <Card className="workspace-data-card">
      <div className="workspace-viewbar"><Segmented value={orderView} onChange={setOrderView} options={Object.keys(statusLabels)} /><span className="workspace-result-count">当前页 {orders.length} 笔订单</span></div>
      <Table columns={columns} dataSource={orders} rowKey="id" loading={loading} scroll={{ x: 1100 }} pagination={{ ...pagination, onChange: (page, pageSize) => fetchOrders(page, pageSize) }} />
    </Card>

    <Drawer title="订单详情" width={760} open={detailOpen} onClose={() => setDetailOpen(false)}>
      {detail && <>
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="订单号" span={2}>{detail.order_no}</Descriptions.Item>
          <Descriptions.Item label="订单状态">{statusMap[detail.order_status]?.text || '未知'}</Descriptions.Item>
          <Descriptions.Item label="支付状态">{Number(detail.pay_status) === 1 ? '已支付' : Number(detail.pay_status) === 2 ? '已退款' : '未支付'}</Descriptions.Item>
          <Descriptions.Item label="商品金额">¥{Number(detail.total_amount || 0).toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="优惠金额">-¥{Number(detail.discount_amount || 0).toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="运费">¥{Number(detail.freight_amount || 0).toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="实付金额">¥{Number(detail.pay_amount || 0).toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="收货人">{detail.receiver_name} {detail.receiver_phone}</Descriptions.Item>
          <Descriptions.Item label="收货地址" span={2}>{detail.receiver_address}</Descriptions.Item>
          <Descriptions.Item label="物流" span={2}>{detail.delivery_company ? `${detail.delivery_company} ${detail.delivery_no}` : '尚未发货'}</Descriptions.Item>
        </Descriptions>
        <Table style={{ marginTop: 20 }} size="small" pagination={false} rowKey="id" dataSource={detail.orderItems || []} columns={[
          { title: '商品', render: (_, item) => item.product_name || item.product?.name },
          { title: '单价', dataIndex: 'price', render: v => `¥${Number(v || 0).toFixed(2)}` },
          { title: '数量', dataIndex: 'quantity' },
          { title: '小计', render: (_, item) => `¥${(Number(item.price || 0) * Number(item.quantity || 0)).toFixed(2)}` },
        ]} />
        {(detail.returnRequests || []).map(returnRequest => (
          <Card key={returnRequest.id} size="small" title={returnRequest.return_type === 1 ? '退款信息' : '退货退款信息'} style={{ marginTop: 20 }}>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="售后单号" span={2}>{returnRequest.return_no}</Descriptions.Item>
              <Descriptions.Item label="售后状态"><Tag color={Number(returnRequest.status) === 8 ? 'green' : 'orange'}>{returnStatusMap[returnRequest.status] || '未知'}</Tag></Descriptions.Item>
              <Descriptions.Item label="售后类型">{Number(returnRequest.return_type) === 1 ? '仅退款' : '退货退款'}</Descriptions.Item>
              <Descriptions.Item label="退款金额">¥{Number(returnRequest.return_amount || 0).toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="申请时间">{returnRequest.created_at ? new Date(returnRequest.created_at).toLocaleString() : '-'}</Descriptions.Item>
              <Descriptions.Item label="申请原因" span={2}>{returnRequest.return_reason || '-'}</Descriptions.Item>
              {returnRequest.return_express_no && <Descriptions.Item label="退货物流" span={2}>{returnRequest.return_express_company} {returnRequest.return_express_no}</Descriptions.Item>}
            </Descriptions>
            <Timeline style={{ marginTop: 20 }} items={(returnRequest.statusLogs || []).map(log => ({
              children: `${new Date(log.created_at).toLocaleString()} · ${log.operator_name || '系统'} · ${log.remark || returnStatusMap[log.status] || '状态更新'}`
            }))} />
          </Card>
        ))}
        <Timeline style={{ marginTop: 24 }} items={(detail.logs || []).map(log => ({ children: `${new Date(log.time).toLocaleString()} · ${log.action} · ${log.description}` }))} />
      </>}
    </Drawer>

    <Modal title="订单发货" open={!!shipOrder} onCancel={() => setShipOrder(null)} onOk={submitShip} okText="确认发货">
      <Form form={shipForm} layout="vertical">
        <Form.Item name="deliveryCompany" label="物流公司" rules={[{ required: true, message: '请输入物流公司' }]}><Input maxLength={50} /></Form.Item>
        <Form.Item name="deliveryNo" label="快递单号" rules={[{ required: true, message: '请输入快递单号' }, { pattern: /^[A-Za-z0-9-]{6,40}$/, message: '快递单号格式不正确' }]}><Input /></Form.Item>
        <Form.Item name="remark" label="发货备注"><Input.TextArea rows={3} maxLength={200} /></Form.Item>
      </Form>
    </Modal>
  </div>;
};

export default OrderList;
